import React, {Fragment, useState, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {Breadcrumbs, Loading} from 'worksource-ui';
import {HTMLTable, Tag, InputGroup} from '@blueprintjs/core';
import {gql} from 'apollo-boost';
import {useQuery} from '@apollo/react-hooks';

import FileEditDialog from 'dialogs/FileEditDialog';
import {useUIActions} from 'utils/ui-context';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import withMainLayout from 'layouts/main/view';

const FETCH_FILES = gql`
  query FilesIndexPageFetch($clientId: ID!) {
    files(clientId: $clientId) {
      id,
      name,
      identifier,
      guides {
        id,
        name,
      }
    }
  }
`;

const FilesIndexPage = () => {
  ////
  // State/hooks
  //
  const {clientId} = useParams();
  const {setDialog} = useUIActions();
  const [searchQuery, setSearchQuery] = useState('');
  const {loading: loadingFetch, data, refetch} = useQuery(FETCH_FILES, {
    variables: {clientId},
    onError: error => showGQLError({error}),
    fetchPolicy: 'network-only',
  });

  const files = useMemo(() => {
    if (searchQuery.length === 0) {
      return data?.files;
    }

    const normalize = (str) => {
      if (!str) return '';
      return str.toLowerCase();
    };

    const normQuery = normalize(searchQuery);
    return data.files.filter((file) => {
      return normalize(file.name).includes(normQuery)
        || normalize(file.identifier).includes(normQuery);
    });
  }, [searchQuery, data?.files]);

  ////
  // Events
  //
  const openFile = (fileId) => {
    setDialog(() => (
      <FileEditDialog
        clientId={clientId}
        fileId={fileId}
        onSuccess={() => refetch()}
      />
    ));
  };

  ////
  // Render
  //
  if (loadingFetch || !data) {
    return (
      <Loading />
    );
  }

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/files`}
            icon="document"
            text="Files"
          />
        </Breadcrumbs>
        <div className="spacer" />
        <InputGroup
          leftIcon="search"
          placeholder="Search by name or part #"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </HeaderBar>
      <div className="content-container">
        <div className="layout--full">
          <HTMLTable striped interactive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Part #</th>
                <th>Guides</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => {
                return (
                  <tr key={file.id} onClick={() => openFile(file.id)}>
                    <td><span title={file.name} className="files-index--name">{file.name}</span></td>
                    <td>{file.identifier || 'N/A'}</td>
                    {file.guides.length > 0 && (
                      <td className="files-index--guides">
                        {file.guides.slice(0, 3).map(({id, name}) => (
                          <Tag minimal key={id}>{name}</Tag>
                        ))}
                        {file.guides.length > 3 && (
                          <Tag minimal>{file.guides.length - 3} more...</Tag>
                        )}
                      </td>
                    )}
                    {file.guides.length === 0 && (
                      <td>
                        None
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </HTMLTable>
          <div className="files-index--stats">
            {data.files.length !== files.length && (
              <div className="text">
                Showing {files.length} of {data.files.length} files
              </div>
            )}
            {data.files.length === files.length && (
              <div className="text">
                {files.length} files
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout()(FilesIndexPage);
